import { Dialog as DialogPrimitive } from "radix-ui";
import { Trans, useTranslation } from "react-i18next";

import { ContactUsDialog } from "~/components/contact-us-dialog";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { InformationIcon } from "~/components/ui/icons/information";
import {
  ModalClose,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "~/components/ui/modal";
import type { OrdersByTrackingQuery } from "~/graphql/graphql-new.gen";

import { OrderDetails } from "./order-details";

type ModalAddTrackingAlreadyAssignedDifferentRecipientProps = {
  detectedOrders: OrdersByTrackingQuery["ordersByTracking"];
};

export const ModalAddTrackingAlreadyAssignedDifferentRecipient: React.FC<
  ModalAddTrackingAlreadyAssignedDifferentRecipientProps
> = ({ detectedOrders }) => {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-auto overflow-y-auto p-6 md:p-10">
        <ModalHeader>
          <ModalTitle>
            {t("order.tracking.modal_add_tracking_number.add_tracking_number")}
          </ModalTitle>
        </ModalHeader>
        <div className="space-y-10">
          <Alert>
            <InformationIcon />
            <AlertTitle>
              {t(
                "order.tracking.modal_add_tracking_number.x_tracking_already_exists",
                { count: detectedOrders.length },
              )}
            </AlertTitle>
            <AlertDescription>
              {t(
                "order.tracking.modal_add_tracking_number.impossible_to_associate_tracking_different_customers",
              )}
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <p className="text-sm text-gray-900">
              <Trans
                t={t}
                i18nKey="order.tracking.x_order_of"
                values={{
                  customer: detectedOrders[0]?.recipient.fullName,
                  count: detectedOrders.length,
                }}
                components={{ b: <b className="text-gray-900" /> }}
              />
            </p>
            <ul className="divide-y divide-gray-300 rounded border border-gray-300">
              {detectedOrders.map((order) => (
                <li key={order.id}>
                  <OrderDetails order={order} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <ModalFooter className="mt-4 justify-between">
        <div className="flex items-center space-x-1 text-sm">
          <span className="text-gray-900">
            {t("order.tracking.modal_add_tracking_number.have_a_problem")}
          </span>
          <DialogPrimitive.Root modal={false}>
            <DialogPrimitive.Trigger asChild>
              <button type="button" className="font-semibold text-green-900">
                {t("order.actions_contact_us_via_chat")}
              </button>
            </DialogPrimitive.Trigger>
            <ContactUsDialog />
          </DialogPrimitive.Root>
        </div>
        <div className="space-x-6">
          <ModalClose asChild>
            <Button type="button">{t("close")}</Button>
          </ModalClose>
        </div>
      </ModalFooter>
    </div>
  );
};
